<script setup lang="ts">
const props = defineProps({
	label: {
		type: String,
		default: undefined,
	},
	required: {
		type: Boolean,
		default: false,
	},
	readonly: {
		type: Boolean,
		default: false,
	},
	placeholder: {
		type: String,
		default: undefined,
	},
	modelValue: {
		type: [String, Number],
		default: 0,
	},
	modelModifiers: {
		type: Object,
		default: () => ({}),
	},
	errorMessage: {
		type: String,
		default: undefined,
	},
	isShowCustomError: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	step: {
		type: Number,
		default: 1,
	},
	max: {
		type: Number,
		required: true,
	},
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: string | number): void
	(e: 'focus'): void
	(e: 'blur'): void
	(e: 'change'): void
}>();

const inputElement = ref<HTMLInputElement | null>(null);
const isFocus = ref(false);

const isDisabledLeftButton = computed(() => {
	return props.disabled || Number(props.modelValue) <= 0;
});

const isDisabledRightButton = computed(() => {
	return props.disabled || Number(props.modelValue) >= props.max;
});

function handleClickLabel() {
	inputElement.value?.focus();
}

function handleInput(event: Event) {
	const targetElement = event.target as HTMLInputElement;
	let value: string | number = targetElement.value.toString();

	// TODO: Add support for comma separator
	if (props.modelModifiers.integer) {
		value = processInteger(value, props.modelValue) || '';
	} else if (props.modelModifiers.float) {
		value = processFloat(value);
	}

	targetElement.value = value?.toString().trim() ?? '';
	emit('update:modelValue', value);
}

function handleFocus() {
	isFocus.value = true;
	emit('focus');
}

function handleBlur() {
	isFocus.value = false;
	emit('blur');
}

function handleChange(e: Event) {
	// Trim space
	const target = e.target as HTMLInputElement;
	const trimValue = target.value.trim();
	emit('update:modelValue', trimValue);
	emit('change');
}

function handleIncrement() {
	const currentValue = Number(props.modelValue as string) || 0;
	if (currentValue >= props.max) {
		return;
	}

	const newValue = currentValue + props.step;
	emit('update:modelValue', newValue);
}

function handleDecrement() {
	const currentValue = Number(props.modelValue as string) || 0;
	if (currentValue <= 0) {
		return;
	}

	const newValue = currentValue - props.step;
	emit('update:modelValue', newValue);
}

const isError = computed(() => {
	return !!props.errorMessage;
});
</script>

<template>
  <div class="input-container">
    <label
      v-if="label"
      class="label text-sm text-medium"
      @click="handleClickLabel"
    >
      {{ label }}
    </label>
    <div class="stepper-container">
      <button
        class="button-stepper left-btn"
        :disabled="isDisabledLeftButton"
        @click="handleDecrement"
      >
        <MinusIcon :color="isDisabledLeftButton ? ICON_COLOR.CURRENT_COLOR : ICON_COLOR.DEFAULT" />
      </button>
      <div
        :class="[
          'text-field-wrapper',
          {
            '--focus': isFocus,
            '--error': isError,
            '--disabled': disabled
          }
        ]"
      >
        <input
          ref="inputElement"
          :value="modelValue"
          :required="required"
          :readonly="readonly"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="[
            'text-field',
            {
              '--error': isError,
            }
          ]"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
        >
      </div>
      <button
        class="button-stepper right-btn"
        :disabled="isDisabledRightButton"
        @click="handleIncrement"
      >
        <PlusIcon :color="isDisabledRightButton ? ICON_COLOR.CURRENT_COLOR : ICON_COLOR.DEFAULT" />
      </button>
    </div>
    <p
      v-if="!isShowCustomError"
      class="error-message"
    >
      {{ errorMessage }}
    </p>
    <p
      v-else-if="$slots['helperText']"
      class="helper-text"
    >
      <slot name="helperText" />
    </p>
  </div>
</template>

<style lang="scss" scoped>
.input-container {
  display: flex;
  flex-direction: column;

  .label {
    color: inherit;
  }

  .stepper-container {
    display: flex;
    justify-content: space-between;

    .button-stepper {
      width: rem(40);
      height: rem(40);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid colors-get(gray, 300);
      box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
      cursor: pointer;
      z-index: 2;

      &.left-btn {
        border-radius: 8px 0 0 8px;
        border-right-width: 0;
      }

      &.right-btn {
        border-radius: 0 8px 8px 0;
        border-left-width: 0;
      }

      &:hover {
        background-color: colors-get(gray, 50);
      }

      &:disabled {
        color: colors-get(gray, 300);
        background-color: colors-get(base, white);
        border: 1px solid colors-get(gray, 200);
        cursor: default;
      }

      &:focus {
        color: colors-get(gray, 900);
        border: 1px solid colors-get(gray, 300);
        box-shadow:
          0px 0px 0px 4px #F2F4F7,
          0px 1px 2px 0px rgba(16, 24, 40, 0.05);
      }
    }
  }

  .error-message {
    text-align: left;
  }

  .text-field-wrapper {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    border: 1px solid colors-get(gray, 300);
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    box-sizing: border-box;

    .text-field {
      position: relative;
      padding: rem(10) rem(4);
      width: rem(44);
      height: rem(38);
      text-align: center;
      @include fonts-get(regular, text-md);
      color: colors-get(gray, 900);
      background: colors-get(base, white);
      outline: none;
      cursor: inherit;
      border: none;

      &:disabled {
        background: colors-get(gray, 50);
        color: colors-get(gray, 500);

        &::placeholder {
          color: colors-get(gray, 400);
        }
      }

      &::placeholder {
        color: colors-get(gray, 400);
      }
    }

    &.--focus {
      color: colors-get(gray, 900);
      border: 1px solid colors-get(gray, 300);
      box-shadow:
        0px 0px 0px 4px #F2F4F7,
        0px 1px 2px 0px rgba(16, 24, 40, 0.05);
      z-index: 3;
    }

    &.--error {
        border: 1px solid colors-get(error, 300);

      &.--focus {
        box-shadow:
          0px 0px 0px 4px #FEE4E2,
          0px 1px 2px 0px rgba(16, 24, 40, 0.05);
      }
    }

    &.--disabled {
      background: colors-get(gray, 50);
    }
  }
}
</style>
